import { useEffect, useState } from 'react';
import { displayHost, homeLeague } from '../lib/host';
import { api, type League, type Sponsor } from '../lib/api';
import { platformOnly as platformOnlySponsors } from '../lib/sponsors';
import PoweredBy from './PoweredBy';

/**
 * League sponsors, shown on every page.
 *
 * The strip sits on a white board rather than directly on the navy. Sponsor
 * artwork arrives in every conceivable form and most brand guidelines specify a
 * white background — dropping a dark-text logo straight onto navy would make it
 * invisible, and recolouring someone else's mark is not ours to do. A white
 * board also reads like courtside advertising, which is the right idiom.
 *
 * Renders nothing at all until there are sponsors, so it costs no space while
 * the league has none.
 */
export default function SponsorStrip({
  seasonId,
  league,
}: {
  seasonId?: number;
  league?: League | null;
}) {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [platform, setPlatform] = useState<Sponsor[]>([]);
  // How big the platform is, counted by the endpoint that serves its sponsors.
  const [platformReach, setPlatformReach] = useState<{ players: number; leagues: number } | null>(
    null,
  );

  // 8Ball's own sponsors, on a league hosted for nothing. Kept separate from
  // the league's and labelled as ours: presenting them as this league's would
  // misstate it to their players, and to sponsors who bought another league's
  // audience. Which league carries them is a commercial fact stored on the
  // league, so it changes when money changes hands, not with a deploy.
  const carries = league?.showsPlatformSponsors ?? false;
  useEffect(() => {
    if (!carries) {
      setPlatform([]);
      setPlatformReach(null);
      return;
    }
    api
      .sponsors()
      .then((data) => {
        setPlatform(data.sponsors);
        setPlatformReach(data.reach ?? null);
      })
      .catch(() => {
        setPlatform([]);
        setPlatformReach(null);
      });
  }, [carries]);

  useEffect(() => {
    // Wait for the season. Asking without one returns every sponsor on the
    // platform, including sponsors of the platform itself, which would flash
    // another party's logo into this league's footer before correcting itself.
    if (!seasonId) {
      setSponsors([]);
      return;
    }
    api
      .sponsors(seasonId)
      .then((data) => setSponsors(data.sponsors))
      // A sponsor strip is never worth surfacing an error for.
      .catch(() => setSponsors([]));
  }, [seasonId]);

  // A sponsor backing both the league and the platform appears once, in the
  // league's own strip, rather than twice on one page.
  const platformOnly = platformOnlySponsors(platform, sponsors);

  // 8Ball's card always shows — it is the platform credit, not a sponsor — and
  // it sits between the league's own sponsors and 8Ball's, so the page reads:
  // the league's backers, then who brought them the app, then who backs us.

  /*
   * What this league can actually prove about its audience. Undefined until the
   * season's counts arrive, in which case the card says the same thing without
   * a number rather than guessing one.
   */
  const season = league?.seasons.find((s) => s.id === seasonId);
  const counts = season?.stats;
  const reach =
    counts && counts.players > 0 && counts.teams > 0
      ? {
          players: counts.players,
          teams: counts.teams,
          unit: (counts.teams === 1
            ? league?.unitNounSingular ?? 'team'
            : league?.unitNoun ?? 'teams'
          ).toLowerCase(),
        }
      : null;

  const headline = sponsors.filter((s) => s.tier === 1);
  const rest = sponsors.filter((s) => s.tier > 1);

  return (
    <>
      {/*
        A league with no sponsors of its own has a gap here. Rather than leave
        it empty, the space says what it is — in front of every player, every
        week. Inventory that advertises itself.

        Only while the league is on the free arrangement. A league that pays has
        bought this space, and selling it back to them on their own page would
        be absurd; a league that already has sponsors sees theirs instead.
      */}
      {sponsors.length === 0 && carries && (
        <section className="sponsors" aria-label="Sponsorship">
          <p className="sponsors-label">Sponsorship</p>
          <div className="sponsor-open">
            <p className="open-lead">This space is available</p>
            <p className="open-note">
              {/*
                Counted, not claimed. The league's own squad and side numbers,
                read live from the season — so the figure a sponsor is quoted is
                one they could check by asking the league, and it corrects
                itself when a house signs a player.

                Nothing on this platform counts visitors, so no viewership
                number can be stated here honestly. Players is a floor rather
                than a reach figure, and a floor that is true beats a reach
                figure that is invented.
              */}
              {reach
                ? `Your brand in front of all ${reach.players} players across ${reach.teams} ` +
                  `${reach.unit}, every week of the season.`
                : 'Your brand in front of every player in the league, every week of the season.'}
            </p>
            <p className="open-site">{displayHost(window.location.hostname)}</p>
          </div>
        </section>
      )}

      {sponsors.length > 0 && (
    <section className="sponsors" aria-label="League sponsors">
      <p className="sponsors-label">Our sponsors</p>
      <div className="sponsor-board">
        <div className="sponsor-row">
          {headline.map((sponsor) => (
            <SponsorLogo key={sponsor.id} sponsor={sponsor} />
          ))}
        </div>
        {rest.length > 0 && (
          <div className="sponsor-row small">
            {rest.map((sponsor) => (
              <SponsorLogo key={sponsor.id} sponsor={sponsor} />
            ))}
          </div>
        )}
      </div>
    </section>
      )}

      <PoweredBy offPlatform={Boolean(homeLeague(window.location.hostname, league ? [league] : []))} />

      {platformOnly.length > 0 && (
        <section className="sponsors platform-backed" aria-label="8Ball Padel sponsors">
          <p className="sponsors-label">8Ball Padel is supported by</p>
          <div className="sponsor-board">
            <div className="sponsor-row small">
              {platformOnly.map((sponsor) => (
                <SponsorLogo key={`p${sponsor.id}`} sponsor={sponsor} />
              ))}
            </div>
          </div>
          {/*
            What a sponsor in THIS strip reaches, as distinct from a league's own
            space above it. This one is carried by every league hosted for
            nothing, so its audience is the whole platform rather than one
            league — a larger thing to sell, and the number says so.

            Counted, not claimed, and it grows as leagues join.
          */}
          {platformReach && platformReach.players > 0 && (
            <p className="platform-reach">
              {platformReach.players} players across {platformReach.leagues}{' '}
              {platformReach.leagues === 1 ? 'league' : 'leagues'}
            </p>
          )}
        </section>
      )}
    </>
  );
}

function SponsorLogo({ sponsor }: { sponsor: Sponsor }) {
  const image = sponsor.logoUrl ? (
    <img src={sponsor.logoUrl} alt={sponsor.name} loading="lazy" decoding="async" />
  ) : (
    <span className="sponsor-name">{sponsor.name}</span>
  );

  if (!sponsor.websiteUrl) return <div className="sponsor">{image}</div>;

  return (
    <a
      className="sponsor"
      href={sponsor.websiteUrl}
      target="_blank"
      // noopener/noreferrer: these are third-party sites we do not control.
      rel="noopener noreferrer sponsored"
    >
      {image}
    </a>
  );
}
